import type { BatchRegisterRow, SiteOrder } from '@api/vendor-portal';

import { remaining } from '@components/containers/vendorHome/orderTotals';
import { groupLinesBy } from '@utils/rowTree';

export interface ProductionRow {
    key: string;
    /** 'order' on a header row, 'line' on a SKU beneath it — renderers branch on this. */
    kind: 'order' | 'line';
    orderCode: string;
    lineCode: string;
    workOrderId: string;
    productId: string;
    productName: string;
    ordered: number;
    manufactured: number;
    /**
     * Where the goods are NOW: cleared and still here, and declared but unruled.
     *
     * Neither is cumulative — a batch that passed and then went has left `qcApproved` for
     * `sent`, so no unit is counted in two columns at once.
     */
    qcApproved: number;
    /** Shelf-stock cover folded into the figures, kept apart so cells can mark it. */
    allocatedFromStock?: { total: number; approved: number; pending: number } | null;
    qcPending: number;
    sent: number;
    /** Ordered minus sent: what is still owed. Floored per line — see openQuantity. */
    openQuantity: number;
    dueDate: string;
    /** When the brand placed the order. Order rows only; a line is not placed separately. */
    orderDate: string | null;
    status: string;
    /** Batch codes run against this line, newest first. Empty when nothing has been started. */
    batchCodes: string[];

    /**
     * The order this line belongs to, when it belongs to one.
     *
     * NULL IS ORDINARY. A run commissioned on its own has no order header — `orderCode` then
     * shows the work order's own code — and there is no document behind it to open.
     */
    productionOrderId: string | null;
    /** Whether the brand issued a PDF the site can open against this order. */
    hasDocument: boolean;
    /**
     * Text the grid's search should match on but no column shows — the order codes gathered
     * under a product, when the sheet is grouped by SKU.
     *
     * DataSheet searches the values of a TOP-LEVEL row and only walks into children once they
     * are expanded, so anything that moved a level down stops being findable. This puts it back
     * without spending a column on it.
     */
    searchBlob?: string;
    lines?: ProductionRow[];
}

/** How many codes to name before falling back to a count. */
const NAMED_BATCHES = 2;

/**
 * Batch codes as one cell.
 *
 * A line can carry any number of runs, and naming them all turns a column into a paragraph.
 * Two is enough to recognise the work; past that the count is the useful part, and the Batches
 * tab is one click away for the full list.
 */
export function formatBatchCodes(codes: string[]): string {
    if (codes.length === 0) return '—';
    if (codes.length <= NAMED_BATCHES) return codes.join(', ');
    return `${codes.slice(0, NAMED_BATCHES).join(', ')} +${codes.length - NAMED_BATCHES}`;
}

/**
 * One row per SKU a site has been asked to make.
 *
 * THE GRAIN IS THE ORDER LINE, not the batch — the question this table answers is "what am I on
 * the hook for", and a line with three runs against it is still one commitment. Batches ride
 * along in a column rather than splitting the row, so the ordered and manufactured figures stay
 * comparable down the column instead of repeating once per run.
 *
 * Joined client-side on workOrderId, which both reads already carry. No new endpoint: the
 * orders come from the same call the home screen uses and the batches from the register the
 * Batches tab shows.
 */
/** Rolled up from the lines, because an order has no figures of its own. */
const sum = (rows: ProductionRow[], pick: (row: ProductionRow) => number) =>
    rows.reduce((total, row) => total + (pick(row) || 0), 0);

export function buildProductionRows(
    orders: SiteOrder[],
    batches: BatchRegisterRow[],
): ProductionRow[] {
    const byWorkOrder = new Map<string, string[]>();
    for (const batch of batches) {
        if (!batch.workOrderId) continue;
        const codes = byWorkOrder.get(batch.workOrderId) ?? [];
        codes.push(batch.code);
        byWorkOrder.set(batch.workOrderId, codes);
    }

    return orders.map((order) => {
        const lines = order.lines ?? [];

        const children: ProductionRow[] = lines.map((line) => ({
            // Prefixed so a parent and a child can never collide on a key, which DataSheet
            // requires and which is easy to get wrong when both carry codes.
            key: `line:${line.workOrderId}`,
            kind: 'line' as const,
            orderCode: '',
            lineCode: line.code,
            workOrderId: line.workOrderId,
            productId: line.productId,
            productName: line.productName ?? line.productId,
            ordered: line.ordered ?? 0,
            manufactured: line.produced ?? 0,
            qcApproved: line.qcApproved ?? 0,
            qcPending: line.qcPending ?? 0,
            allocatedFromStock: line.allocatedFromStock ?? null,
            sent: line.dispatched ?? 0,
            openQuantity: remaining(line.ordered ?? 0, line.produced ?? 0),
            dueDate: line.dueDate,
            orderDate: null,
            status: line.status,
            batchCodes: byWorkOrder.get(line.workOrderId) ?? [],
            // The document hangs off the order, and on a grouped table that is the parent row.
            productionOrderId: null,
            hasDocument: false,
        }));

        /*
         * A HEADERLESS WORK ORDER STAYS FLAT. A run commissioned on its own arrives carrying its
         * own code as the order's, and nesting it would put a disclosure triangle in front of a
         * group of one.
         */
        if (!order.productionOrderId) {
            const [only] = children;
            return {
                ...(only ?? {
                    key: `wo:${order.code}`,
                    kind: 'line' as const,
                    lineCode: order.code,
                    workOrderId: '',
                    productId: '',
                    productName: '',
                    ordered: 0,
                    manufactured: 0,
                    qcApproved: 0,
                    qcPending: 0,
                    sent: 0,
                    openQuantity: 0,
                    dueDate: '',
                    orderDate: null,
                    status: order.status,
                    batchCodes: [],
                }),
                key: `wo:${order.code}`,
                orderCode: order.code,
                productionOrderId: null,
                hasDocument: false,
            };
        }

        return {
            key: `order:${order.productionOrderId}`,
            kind: 'order' as const,
            orderCode: order.code,
            lineCode: '',
            workOrderId: '',
            productId: '',
            productName: `${children.length} SKU${children.length === 1 ? '' : 's'}`,
            ordered: sum(children, (line) => line.ordered),
            manufactured: sum(children, (line) => line.manufactured),
            qcApproved: sum(children, (line) => line.qcApproved),
            qcPending: sum(children, (line) => line.qcPending),
            allocatedFromStock: children.some((line) => line.allocatedFromStock?.total)
                ? {
                      total: sum(children, (line) => line.allocatedFromStock?.total ?? 0),
                      approved: sum(children, (line) => line.allocatedFromStock?.approved ?? 0),
                      pending: sum(children, (line) => line.allocatedFromStock?.pending ?? 0),
                  }
                : null,
            sent: sum(children, (line) => line.sent),
            // Summed from the lines rather than ordered-minus-sent at the order level: the
            // per-line floor is what stops an over-shipped SKU cancelling a short one.
            openQuantity: sum(children, (line) => line.openQuantity),
            dueDate: order.expectedCompletionDate ?? '',
            orderDate: order.orderDate,
            status: order.status,
            // The order's own runs are listed under its lines; naming them twice would say the
            // header had runs of its own.
            batchCodes: [],
            productionOrderId: order.productionOrderId,
            hasDocument: order.hasDocument,
            lines: children,
        };
    });
}

/**
 * The same rows, filed under their product instead of their order.
 *
 * A REARRANGEMENT, NOT A SECOND BUILD. The children are the very line rows the order view
 * renders — `groupLinesBy` regroups them — so the two views cannot disagree about a figure.
 * Every parent figure is summed from the children below it, exactly as the order parent is.
 *
 * KEYED ON `productId`, NEVER THE NAME. Two SKUs can carry the same name — a renamed product, a
 * colourway entered twice — and folding on what is printed would merge two commitments into one
 * row whose figures belong to neither.
 *
 * `orderDate` AND `status` GO BLANK ON THE PARENT, because neither is true of a product wanted
 * by two orders: it was not placed on one date, and a SKU on an ISSUED order and an
 * IN_PRODUCTION one has no single status. Both stay on the order rows, where they are facts.
 * `dueDate` becomes the earliest, which is the one that decides what gets made next.
 */
export function toSkuProductionRows(rows: ProductionRow[]): ProductionRow[] {
    return groupLinesBy(
        rows,
        (line) => line.productId,
        // The order's identity moves down onto the line, which is now the only row that shows
        // one — including `hasDocument`, so the PDF link follows the code rather than staying
        // on a parent that no longer represents a single order.
        (line, order) => ({
            ...line,
            orderCode: order.orderCode,
            productionOrderId: order.productionOrderId,
            hasDocument: order.hasDocument,
        }),
    ).map(({ key, lines }) => {
        const [first] = lines;
        const dueDates = lines.map((line) => line.dueDate).filter(Boolean);

        return {
            key: `sku:${key}`,
            kind: 'order' as const,
            /*
             * The product sits in the Order column and the codes move under it — the column
             * renames to "Product / Order" in this mode. The document link needs no change:
             * it already keys off `productionOrderId`, which a product spanning orders has not
             * got, so a parent renders as plain text on its own.
             */
            orderCode: first?.productName ?? key,
            lineCode: '',
            workOrderId: '',
            productId: key,
            productName: `${lines.length} order${lines.length === 1 ? '' : 's'}`,
            ordered: sum(lines, (line) => line.ordered),
            manufactured: sum(lines, (line) => line.manufactured),
            qcApproved: sum(lines, (line) => line.qcApproved),
            qcPending: sum(lines, (line) => line.qcPending),
            allocatedFromStock: lines.some((line) => line.allocatedFromStock?.total)
                ? {
                      total: sum(lines, (line) => line.allocatedFromStock?.total ?? 0),
                      approved: sum(lines, (line) => line.allocatedFromStock?.approved ?? 0),
                      pending: sum(lines, (line) => line.allocatedFromStock?.pending ?? 0),
                  }
                : null,
            sent: sum(lines, (line) => line.sent),
            // Already floored per line by the builder above; summing the floored figures is what
            // stops an over-made SKU on one order cancelling a shortfall on another.
            openQuantity: sum(lines, (line) => line.openQuantity),
            dueDate: dueDates.length > 0 ? dueDates.reduce((a, b) => (a < b ? a : b)) : '',
            orderDate: null,
            status: '',
            // Every run against this product, however many orders they answer.
            batchCodes: lines.flatMap((line) => line.batchCodes),
            productionOrderId: null,
            hasDocument: false,
            /**
             * The order codes, so search still finds them.
             *
             * DataSheet filters the TOP-LEVEL rows and only then walks into the expanded ones,
             * so a child is never matched on its own. Without this, typing an order code in
             * this mode would return nothing — the code having moved a level down — while the
             * same search worked fine in the other mode.
             */
            searchBlob: lines.map((line) => line.orderCode).join(' '),
            lines,
        };
    });
}
